/**
 * What the Admin is allowed to claim about where content came from.
 *
 * The editor tracks a "session source" while someone works: it starts from the
 * status label shown for the stored record and changes as the text is edited.
 * That round trip through a display label is lossy, so the session source can
 * end up as a value the service has never heard of.
 *
 * The rule this module enforces is that an unrecognized session source produces
 * no claim at all. Omitting the field is meaningful to the service: it means
 * "decide from the stored record", which preserves provenance when the text did
 * not change and records a user edit when it did. Sending a guess instead is
 * what breaks: `user-provided` invents authorship the Admin cannot verify, and
 * `unknown` gets persisted verbatim as the content's provenance.
 *
 * Provenance is therefore decided by the service, which can compare against
 * what is stored. This module only forwards a claim it can actually justify.
 */
import { IHighlightSource } from './types'

/**
 * Legacy value for AI-customized content. Still emitted by the editor and still
 * present in stored records, but not part of the service's highlight contract.
 */
export const LEGACY_AI2_SOURCE = 'ai2'

const normalize = (sessionSource: unknown): string =>
  typeof sessionSource === 'string' ? sessionSource.trim().toLowerCase() : ''

/**
 * The highlight source to send, or `undefined` to omit the field.
 *
 * Emptied content reports `none` regardless of the session source, because the
 * removal is unambiguous. Otherwise only a recognized source is forwarded.
 */
export const computeHighlightSource = (
  sessionSource: unknown,
  isEmpty: boolean,
): IHighlightSource | undefined => {
  if (isEmpty) return IHighlightSource.None

  switch (normalize(sessionSource)) {
    case LEGACY_AI2_SOURCE:
    case IHighlightSource.CustomAi:
      return IHighlightSource.CustomAi
    case IHighlightSource.AutoAi:
      return IHighlightSource.AutoAi
    case IHighlightSource.UserProvided:
      return IHighlightSource.UserProvided
    // Missing, unknown, the retired `ai-translated`, `none` alongside content
    // that is not empty, and anything unrecognized: no claim.
    default:
      return undefined
  }
}

/**
 * Description sources the Admin may claim. Unlike highlights, `ai2` is a real
 * description source: the service reads it as the signal to promote the AI
 * draft, so it is forwarded rather than translated.
 *
 * `ai-translated` is absent on purpose. It is provenance older records were
 * given when their content was translated, and echoing it back would ask the
 * service to keep a state nothing assigns any more. Omitting the field instead
 * lets the service decide from the record it already has.
 */
const DESCRIPTION_SOURCES: readonly string[] = [
  'provided',
  'auto-ai',
  LEGACY_AI2_SOURCE,
  'user-provided',
  'missing',
]

/**
 * The description source to send, or `undefined` to omit the field.
 *
 * The service treats an absent or empty source the same way it treats an absent
 * highlight source, so an unrecognized session source is dropped here rather
 * than persisted as the content's provenance.
 */
export const computeDescriptionSource = (sessionSource: unknown): string | undefined => {
  const source = normalize(sessionSource)
  return DESCRIPTION_SOURCES.includes(source) ? source : undefined
}
